import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { ACCENT_BLUE, ACCENT_BLUE_DARK } from '../../Assessments/Workspace/qcTheme';

// Point tallies for one assessment; both values save together and are audit-logged, so Save needs both.

const Header = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '22px 26px 16px',
  borderBottom: '1px solid rgba(0,0,0,0.12)',
});

const Title = styled('h2')({
  fontSize: '1.25rem',
  fontWeight: 400,
  color: 'rgba(0,0,0,0.87)',
  margin: 0,
});

const Subject = styled('p')({
  fontSize: '0.85rem',
  color: 'rgba(0,0,0,0.6)',
  margin: '6px 0 0',
});

const CloseButton = styled('button')({
  border: 'none',
  background: 'none',
  padding: 0,
  display: 'inline-flex',
  flex: 'none',
  cursor: 'pointer',
  color: 'rgba(0,0,0,0.55)',
  '&:hover': { color: 'rgba(0,0,0,0.8)' },
});

const Body = styled('div')({
  padding: '20px 26px 8px',
});

const FieldRow = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '14px',
});

const FieldLabel = styled('div')({
  fontSize: '0.7rem',
  letterSpacing: '1px',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.6)',
  textTransform: 'uppercase',
  marginBottom: '8px',
});

const PointsInput = styled('input')({
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid rgba(0,0,0,0.42)',
  borderRadius: '4px',
  padding: '9px 12px',
  fontFamily: 'inherit',
  fontSize: '1rem',
  color: 'rgba(0,0,0,0.87)',
  outline: 'none',
  '&:focus': {
    borderColor: ACCENT_BLUE,
    boxShadow: `inset 0 0 0 1px ${ACCENT_BLUE}`,
  },
});

const AttributionNote = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '9px',
  background: 'rgba(25,118,210,0.06)',
  border: '1px solid rgba(25,118,210,0.22)',
  borderRadius: '4px',
  padding: '10px 12px',
  marginTop: '16px',
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
    color: ACCENT_BLUE,
    marginTop: '1px',
    flex: 'none',
  },
});

const NoteText = styled('span')({
  fontSize: '0.8rem',
  lineHeight: 1.5,
  color: 'rgba(0,0,0,0.72)',
});

const Footer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  padding: '18px 26px 20px',
});

const CancelButton = styled('button')({
  background: 'transparent',
  border: 'none',
  color: ACCENT_BLUE,
  borderRadius: '4px',
  padding: '9px 14px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': { background: 'rgba(25,118,210,0.06)' },
});

const SaveButton = styled('button')({
  background: ACCENT_BLUE,
  color: '#fff',
  border: 'none',
  borderRadius: '4px',
  padding: '9px 18px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': { background: ACCENT_BLUE_DARK },
  '&:disabled': {
    background: 'rgba(0,0,0,0.12)',
    color: 'rgba(0,0,0,0.32)',
    cursor: 'not-allowed',
  },
});

const isWholeNumber = (value: string): boolean => {
  const parsed = Number(value);
  return value.trim() !== '' && Number.isInteger(parsed) && parsed >= 0;
};

export interface PointManagerModalProps {
  open: boolean;
  subject: string;
  pointsDeducted: number | null;
  pointsTotal: number | null;
  adminName: string;
  onSave: (pointsDeducted: number, pointsTotal: number) => void;
  onClose: () => void;
}

export default function PointManagerModal(props: PointManagerModalProps): JSX.Element {
  const { open, subject, pointsDeducted, pointsTotal, adminName, onSave, onClose } = props;
  const [deducted, setDeducted] = useState('');
  const [total, setTotal] = useState('');

  // Seed the draft from the row each time the dialog opens.
  useEffect(() => {
    if (open) {
      setDeducted(String(pointsDeducted ?? 0));
      setTotal(String(pointsTotal ?? 0));
    }
  }, [open, pointsDeducted, pointsTotal]);

  const canSave = isWholeNumber(deducted) && isWholeNumber(total);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-label='Point Manager'
      PaperProps={{ sx: { width: 460, maxWidth: '100%', borderRadius: '4px' } }}
      maxWidth={false}
    >
      <Header>
        <div>
          <Title>Point Manager</Title>
          <Subject>{subject}</Subject>
        </div>
        <CloseButton type='button' aria-label='Close point manager' onClick={onClose}>
          <CloseIcon sx={{ fontSize: 22 }} />
        </CloseButton>
      </Header>
      <Body>
        <FieldRow>
          <div>
            <FieldLabel>Points Deducted</FieldLabel>
            <PointsInput
              aria-label='Points deducted'
              value={deducted}
              onChange={event => setDeducted(event.target.value)}
            />
          </div>
          <div>
            <FieldLabel>Points Total</FieldLabel>
            <PointsInput
              aria-label='Points total'
              value={total}
              onChange={event => setTotal(event.target.value)}
            />
          </div>
        </FieldRow>
        <AttributionNote>
          <HistoryIcon />
          <NoteText>
            Point edits are attributed to {adminName} and appear on the assessment audit log.
          </NoteText>
        </AttributionNote>
      </Body>
      <Footer>
        <CancelButton type='button' onClick={onClose}>Cancel</CancelButton>
        <SaveButton
          type='button'
          disabled={!canSave}
          onClick={() => onSave(Number(deducted), Number(total))}
        >
          Save
        </SaveButton>
      </Footer>
    </Dialog>
  );
}
